import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import '@/App.css';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Hero from '@/components/Hero';
import About from '@/components/About';
import ProductGrid from '@/components/ProductGrid';
import FlagshipBand from '@/components/FlagshipBand';
import Adaptive from '@/components/Adaptive';
import AdaptiveFilter from '@/components/AdaptiveFilter';
import UserFriendlyFeatures from '@/components/UserFriendlyFeatures';
import FieldNotes from '@/components/FieldNotes';
import Newsletter from '@/components/Newsletter';
import Enquiry from '@/components/Enquiry';
import Footer from '@/components/Footer';
import CookieConsent from '@/components/CookieConsent';
import { EBIKES, CAMPING } from '@/data/catalog';
import { HOME } from '@/constants/testIds';
import { FieldNotesIndex, FieldNotePost } from '@/pages/FieldNotesPages';
import { PrivacyPolicy, TermsAndConditions, CookiePolicy, CompliancePolicy, ConditionsOfSale, Disclaimer, ShippingPolicy } from '@/pages/LegalPages';
import { BlogIndex, BlogPost } from '@/pages/BlogPages';
import SourcingPage from '@/pages/SourcingPage';
import GuidesPage from '@/pages/GuidesPage';
import BlogSeriesTeaser from '@/components/BlogSeriesTeaser';
import BothyLogin, { BothyAuthCallback, BothyProtected } from '@/pages/BothyLogin';
import BothyDashboard from '@/pages/BothyDashboard';
import { CheckoutSuccess, CheckoutCancel } from '@/pages/CheckoutPages';
import TrailsPage from '@/pages/TrailsPage';
import SafetyPage from '@/pages/SafetyPage';
import { CampsitesIndex, CampsiteDetail } from '@/pages/CampsitesPages';
import AccountPage from '@/pages/AccountPage';
import ContactPage from '@/pages/ContactPage';
import HeritageEstatePage from '@/pages/HeritageEstatePage';
import ProductPage from '@/pages/ProductPage';
import { UserProvider } from '@/hooks/useUser';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function HomePage() {
  const [activeAdaptiveTag, setActiveAdaptiveTag] = useState(null);

  useEffect(() => {
    axios.get(`${API}/`).catch(() => {});
    if (window.location.hash) {
      const id = window.location.hash.replace('#', '');
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 200);
      }
    }
  }, []);

  return (
    <div className="App">
      <Navigation />
      <main>
        <Hero />
        <About />

        <section id="ebikes-filter" className="pt-14 pb-2">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <AdaptiveFilter
              activeTag={activeAdaptiveTag}
              onChange={setActiveAdaptiveTag}
            />
          </div>
        </section>

        <ProductGrid
          id="ebikes"
          testId={HOME.ebikesSection}
          eyebrow="The Range · E-Bikes"
          title={<>Seven bikes,<br /><span className="italic">honestly vouched for.</span></>}
          lead={"Our flagship \u2014 the EAHORA 2026 Juliet in green, UK EAPC and EN\u00a015194 certified \u2014 sits alongside six further Eahora models we\u2019re listing on an Enquire-only basis. On these six we\u2019re not taking payment until we\u2019ve confirmed with Eahora that your chosen colour and SKU ships as a UK-legal EAPC build. Send us a note and we\u2019ll come back within one working day with the current UK spec, lead time and a proper quote."}
          items={EBIKES}
          columns={3}
          eapcCompliant
          brassBadge
          activeAdaptiveTag={activeAdaptiveTag}
        />
        <FlagshipBand />
        <UserFriendlyFeatures />
        <Adaptive />
        <ProductGrid
          id="camping"
          testId={HOME.campingSection}
          eyebrow="Field Kit · Camping"
          title={<>Kit chosen from<br /><span className="italic">a working life on the hill.</span></>}
          lead="Two honest tools to start with — a proper field knife and a fire-starter kit stamped with our Heritage Estate mark. More kit will join them only once we can vouch for the supplier and the ground it’s survived."
          items={CAMPING}
          columns={4}
        />
        <FieldNotes />
        <BlogSeriesTeaser />
        <Enquiry />
        <Newsletter />
      </main>
      <Footer />
    </div>
  );
}

/**
 * AppRouter — synchronously detects an OAuth return (#session_id=...) before
 * any other route renders, to avoid race conditions with ProtectedRoute.
 *
 * We dispatch on `pathname`: sessions returning to /account are handled by
 * AccountPage itself (customer flow), everything else goes to the Bothy
 * (admin) callback for backwards compatibility.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
 * URLS, THIS BREAKS THE AUTH.
 */
function AppRouter() {
  const location = useLocation();
  const returningFromOAuth = location.hash?.includes('session_id=');
  const isCustomerReturn = location.pathname.startsWith('/account');

  if (returningFromOAuth && !isCustomerReturn) {
    return <BothyAuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/blog" element={<BlogIndex />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/sourcing" element={<SourcingPage />} />
      <Route path="/guides" element={<GuidesPage />} />
      <Route path="/field-notes" element={<FieldNotesIndex />} />
      <Route path="/field-notes/:slug" element={<FieldNotePost />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsAndConditions />} />
      <Route path="/conditions-of-sale" element={<ConditionsOfSale />} />
      <Route path="/shipping" element={<ShippingPolicy />} />
      <Route path="/disclaimer" element={<Disclaimer />} />
      <Route path="/cookies" element={<CookiePolicy />} />
      <Route path="/compliance" element={<CompliancePolicy />} />
      <Route path="/trails" element={<TrailsPage />} />
      <Route path="/safety" element={<SafetyPage />} />
      <Route path="/campsites" element={<CampsitesIndex />} />
      <Route path="/campsites/:slug" element={<CampsiteDetail />} />
      <Route path="/checkout/success" element={<CheckoutSuccess />} />
      <Route path="/checkout/cancel" element={<CheckoutCancel />} />
      <Route path="/account" element={<AccountPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/heritage-estate" element={<HeritageEstatePage />} />
      <Route path="/product/:slug" element={<ProductPage />} />
      <Route path="/the-bothy" element={<BothyLogin />} />
      <Route
        path="/the-bothy/dashboard"
        element={
          <BothyProtected>
            {(user) => <BothyDashboard user={user} />}
          </BothyProtected>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <UserProvider>
        <AppRouter />
        <CookieConsent />
      </UserProvider>
    </BrowserRouter>
  );
}

export default App;
